import React, { useMemo, useState } from 'react';
import { withAuthenticationRequired } from "@auth0/auth0-react";
import { Container, Card, Form, Row, Col, Table, Button, Badge, Modal, Alert } from "react-bootstrap";
import { Spin } from 'antd';
import { FilterOutlined } from '@ant-design/icons';

import PageTitle from "@/common/PageTitle/PageTitle";
import DateRangePicker from "@/common/DateRangePicker/DateRangePicker";
import PaginationRow from "@/components/PaginationRow/PaginationRow";
import { useGetActivityHistoryQuery } from "@/features/activity-history/activityHistoryApi";
import type { ActivityHistoryEntry } from "@/features/activity-history/activityHistoryTypes";
import { buildActivityDetailSections } from "@/features/activity-history/activityDetailsFields";
import styles from "./activity-history.module.scss";

const PAGE_SIZE = 20;

const ActivityHistory: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEntityType, setSelectedEntityType] = useState<string>('all');
  const [selectedActivityType, setSelectedActivityType] = useState<string>('all');
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [page, setPage] = useState(1);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<ActivityHistoryEntry | null>(null);

  const queryParams = useMemo(() => ({
    search: searchTerm.trim() || undefined,
    entityType: selectedEntityType,
    activityType: selectedActivityType,
    startDate: dateRange?.[0] ? dateRange[0].slice(0, 10) : undefined,
    endDate: dateRange?.[1] ? dateRange[1].slice(0, 10) : undefined,
  }), [searchTerm, selectedEntityType, selectedActivityType, dateRange]);

  const {
    data,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetActivityHistoryQuery(queryParams);

  const activities = data?.events ?? [];
  const totalPages = Math.max(1, Math.ceil(activities.length / PAGE_SIZE));
  const pagedActivities = activities.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleOnClickNext = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  const handleOnClickPrev = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleViewDetails = (activity: ActivityHistoryEntry) => {
    setSelectedActivity(activity);
    setShowDetailsModal(true);
  };

  const formatTimestamp = (timestamp: string | null | undefined) => {
    if (!timestamp) return '—';
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return timestamp;
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getEntityClassBadge = (type: string | null | undefined) => {
    const value = type || '—';
    const badgeColors: Record<string, string> = {
      Employee: 'danger',
      Client: 'info',
      Brand: 'primary',
      Support: 'warning',
      System: 'dark',
      Music: 'success',
    };
    return <Badge bg={badgeColors[value] || 'secondary'}>{value}</Badge>;
  };

  const getActivityClassBadge = (type: string | null | undefined, legacy?: string) => {
    const value = type || legacy || '—';
    return <Badge bg="secondary">{value}</Badge>;
  };

  const getStatusBadge = (status: string) => {
    const badgeColors = {
      'success': 'success',
      'error': 'danger',
      'warning': 'warning'
    };
    return <Badge bg={badgeColors[status as keyof typeof badgeColors] || 'secondary'}>{status}</Badge>;
  };

  const formatDetailsPayload = (payload: Record<string, unknown> | null | undefined) => {
    if (!payload || Object.keys(payload).length === 0) return '—';
    return JSON.stringify(payload, null, 2);
  };

  return (
    <Container fluid>
      <Card>
        <Card.Body>
          <Card.Title style={{ display: 'flex', justifyContent: 'space-between' }}>
            <PageTitle title="Activity History" />
            <Button 
              onClick={() => {
                setSearchTerm('');
                setSelectedEntityType('all');
                setSelectedActivityType('all');
                setDateRange(null);
                setPage(1);
                refetch();
              }}
              style={{ 
                background: 'white', 
                color: '#333', 
                border: '1px solid #e0e0e0', 
                borderRadius: '6px',
                padding: '8px 16px',
                fontWeight: '500'
              }}
            >
              <FilterOutlined /> Reset All
            </Button>
          </Card.Title>
          
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label htmlFor="searchTerm">Search</Form.Label>
                <Form.Control
                  id="searchTerm"
                  type="text"
                  placeholder="Search actor, entity, customer #, activity..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(1);
                  }}
                  style={{ height: '47px' }}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label htmlFor="entityType">Entity Type</Form.Label>
                <Form.Select
                  id="entityType"
                  value={selectedEntityType}
                  onChange={(e) => {
                    setSelectedEntityType(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="all">All entities</option>
                  <option value="Employee">Employee</option>
                  <option value="Client">Client</option>
                  <option value="Brand">Brand</option>
                  <option value="Music">Music</option>
                  <option value="Support">Support</option>
                  <option value="System">System</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label htmlFor="activityType">Activity Type</Form.Label>
                <Form.Select
                  id="activityType"
                  value={selectedActivityType}
                  onChange={(e) => {
                    setSelectedActivityType(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="all">All activities</option>
                  <option value="Created">Created</option>
                  <option value="Edited">Edited</option>
                  <option value="AccessPermissions">Access / Permissions</option>
                  <option value="Archived">Archived</option>
                  <option value="Restored">Restored</option>
                  <option value="PermanentDelete">Permanent Delete</option>
                  <option value="SupportMode">Support Mode</option>
                  <option value="Security">Security</option>
                  <option value="System">System</option>
                  <option value="PageView">Page View</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          
          <Row className="mb-3">
            <Col md={8}>
              <Form.Group>
                <Form.Label>Date Range</Form.Label>
                <DateRangePicker
                  onChange={(dates) => {
                    if (dates && dates.length === 2) {
                      setDateRange([
                        dates[0] ? dates[0].toISOString() : '', 
                        dates[1] ? dates[1].toISOString() : ''
                      ]);
                    } else {
                      setDateRange(null);
                    }
                    setPage(1);
                  }}
                  style={{ width: '100%' }}
                />
              </Form.Group>
            </Col>
          </Row>

          {isError && (
            <Alert variant="danger">Failed to load activity history. Please refresh and try again.</Alert>
          )}
          
          <div className={styles.sectionContainer}>
            <div className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>Activity Log ({activities.length} entries)</h3>
            </div>
            
            <Spin spinning={isLoading || isFetching}>
            <Table hover responsive className="legacy-row-height-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>User</th>
                  <th>Entity</th>
                  <th>Activity</th>
                  <th>Activity Type</th>
                  <th>IP Address</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedActivities.map((activity) => (
                  <tr key={`${activity.id}-${activity.timestamp}`}>
                    <td>{formatTimestamp(activity.timestamp)}</td>
                    <td>
                      <div>
                        <strong>{activity.userName || '—'}</strong>
                        <br />
                        <small className="text-muted">
                          {activity.actorEmployeeId || '—'}
                        </small>
                      </div>
                    </td>
                    <td>
                      <div>{getEntityClassBadge(activity.entityClass)}</div>
                      {activity.entityName ? (
                        <div><small><strong>{activity.entityName}</strong></small></div>
                      ) : null}
                      {activity.customerNumber ? (
                        <div><small className="text-muted">{activity.customerNumber}</small></div>
                      ) : null}
                    </td>
                    <td>
                      <div>
                        <strong>{activity.activity}</strong>
                        {activity.activityDetail && (
                          <>
                            <br />
                            <small className="text-muted">{activity.activityDetail}</small>
                          </>
                        )}
                      </div>
                    </td>
                    <td>{getActivityClassBadge(activity.activityClass, activity.activityType)}</td>
                    <td>{activity.ipAddress || '—'}</td>
                    <td>{getStatusBadge(activity.status)}</td>
                    <td>
                      <Button 
                        variant="link" 
                        size="sm" 
                        className={styles.actionLink}
                        onClick={() => handleViewDetails(activity)}
                        title="View Details"
                      >
                        👁️
                      </Button>
                    </td>
                  </tr>
                ))}
                {!isLoading && pagedActivities.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center">
                      No activity found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
            </Spin>
            
            {totalPages > 1 && (
              <PaginationRow
                page={page}
                onPageChange={(p) => setPage(p)}
                totalPages={typeof totalPages === "number" ? totalPages : undefined}
                onClickPrevious={handleOnClickPrev}
                handleOnClickNext={handleOnClickNext}
                disableLeft={page === 1}
                disableNext={page === totalPages}
              />
            )}
          </div>
        </Card.Body>
      </Card>

      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Activity Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedActivity && (() => {
            const sections = buildActivityDetailSections(selectedActivity);
            const renderFields = (fields: { label: string; value: string }[]) =>
              fields.map((field) => (
                <p key={field.label}>
                  <strong>{field.label}:</strong>{" "}
                  {field.label === "Entity Type"
                    ? getEntityClassBadge(selectedActivity.entityClass)
                    : field.label === "Type"
                      ? getActivityClassBadge(
                          selectedActivity.activityClass,
                          selectedActivity.activityType,
                        )
                      : field.label === "Status"
                        ? getStatusBadge(selectedActivity.status)
                        : field.label === "Timestamp"
                          ? formatTimestamp(field.value)
                          : field.value}
                </p>
              ));
            return (
            <div>
              <Row>
                <Col md={6}>
                  <h6>Actor</h6>
                  {renderFields(sections.actor)}
                </Col>
                <Col md={6}>
                  <h6>Activity</h6>
                  {renderFields(sections.activityInfo)}
                </Col>
              </Row>

              {sections.context.length > 0 && (
                <Row className="mt-3">
                  <Col md={12}>
                    <h6>Context</h6>
                    {renderFields(sections.context)}
                  </Col>
                </Row>
              )}

              {sections.technical.length > 0 && (
                <Row className="mt-3">
                  <Col md={12}>
                    <h6>Technical</h6>
                    {renderFields(sections.technical)}
                  </Col>
                </Row>
              )}

              <Row className="mt-3">
                <Col md={12}>
                  <h6>Details Payload</h6>
                  <pre className={styles.detailsPayload}>
                    {formatDetailsPayload(selectedActivity.detailsPayload)}
                  </pre>
                </Col>
              </Row>
            </div>
            );
          })()}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default withAuthenticationRequired(ActivityHistory);
